import type { ReadonlyDeep } from 'type-fest';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { isCancel } from 'axios';
import _cloneDeep from 'lodash/cloneDeep';

import { useUserStore } from '@/stores/useUserStore';
import { fetchGetDashboardCustomer } from '@/helpers/fetchers';

const defCustomerDetailsCache: openapi.CustomerDetailContentOpenapi[] = [];

const usePrivateCustomerDetailsStore = defineStore('_customerDetails', () => {
  const customerDetailsCache = ref<openapi.CustomerDetailContentOpenapi[]>(
    _cloneDeep(defCustomerDetailsCache),
  );

  function $reset(): void {
    customerDetailsCache.value = _cloneDeep(defCustomerDetailsCache);
  }

  return {
    customerDetailsCache,
    $reset,
  };
});

export const useCustomerDetailsStore = defineStore('customerDetails', () => {
  const _store = usePrivateCustomerDetailsStore();
  const userStore = useUserStore();

  let customerDetailsController: AbortController | undefined;

  function $reset(): void {
    _store.$reset();
    if (customerDetailsController) {
      customerDetailsController.abort();
      customerDetailsController = undefined;
    }
  }

  const customerDetails = computed(() => _store.customerDetailsCache);

  /** Get customer details by code from cache or load if not cached */
  async function getCustomerDetails(
    customerCode?: string,
  ): Promise<ReadonlyDeep<openapi.CustomerDetailContentOpenapi> | undefined> {
    let codeToLoad = customerCode;

    // If no code provided, use first available code from user
    if (!codeToLoad) {
      const availableCodes = userStore.user.available_codes;
      if (availableCodes?.length) {
        codeToLoad = availableCodes[0];
      }
    }

    if (!codeToLoad) return undefined;

    const existingDetails = _store.customerDetailsCache.find(
      (c) => c.code === codeToLoad,
    );
    if (existingDetails) {
      return existingDetails;
    }

    if (customerDetailsController) {
      customerDetailsController.abort();
    }
    customerDetailsController = new AbortController();

    try {
      const data = await fetchGetDashboardCustomer(
        { code: codeToLoad },
        { signal: customerDetailsController.signal },
      );

      _store.customerDetailsCache.push(data);

      return data;
    } catch (err) {
      if (isCancel(err)) {
        return undefined;
      }

      const error = err instanceof Error ? err : new Error(String(err));
      throw error;
    }
  }

  /** Preload multiple customer details */
  async function preloadCustomerDetails(
    customerCodes: string[],
  ): Promise<void> {
    const promises = customerCodes.map((code) =>
      getCustomerDetails(code).catch(() => undefined),
    );
    await Promise.all(promises);
  }

  function clearCache(): void {
    _store.customerDetailsCache = [];
  }

  /** Get customer details from cache only (no API call) */
  function getCachedCustomerDetails(
    customerCode: string,
  ): ReadonlyDeep<openapi.CustomerDetailContentOpenapi> | undefined {
    return _store.customerDetailsCache.find((c) => c.code === customerCode);
  }

  function isCustomerDetailsCached(customerCode: string): boolean {
    return _store.customerDetailsCache.some((c) => c.code === customerCode);
  }

  return {
    customerDetails,
    getCustomerDetails,
    preloadCustomerDetails,
    clearCache,
    getCachedCustomerDetails,
    isCustomerDetailsCached,
    $reset,
  };
});
